/* =============================================================================
 * Copyright (c) 2026 SKYXOPS Corp. All rights reserved.
 * ============================================================================= */
/**
 * Composes the left / right slot contents for <TopBar> in launcher.
 * Branding (color override + app name + eyebrow + on-SKYXOPS attribution)
 * lives on the left; principal info + theme toggle + logout on the right.
 *
 * Banner (acting-as-client) is also produced here when the principal is
 * impersonating, so the page-level component can pass it as <TopBar>'s
 * `banner` prop.
 */
import { FormattedMessage } from 'react-intl';
import type { Branding, TilesResponse } from '../api';
import { AccountMenu } from './AccountMenu';

const DEFAULT_APP_NAME = 'SKYXOPS';
// White SKYXOPS logo for the dark header.
const SKYXOPS_LOGO = 'https://appstatic.skyxops.io/skyxops/SKYXOPS-Logo-WebApp.png';

export interface TopBarSlotsProps {
  principal: TilesResponse['principal'] | null;
  branding: Branding | null;
  onLogout: () => void;
}

export function useTopBarSlots({ principal, branding, onLogout }: TopBarSlotsProps) {
  const appName = branding?.app_name ?? DEFAULT_APP_NAME;
  const eyebrow = branding?.eyebrow_text ?? null;
  const hideSkyxops = branding?.hide_skyxops_branding ?? false;
  const headerStyle = branding?.primary_color
    ? { backgroundColor: branding.primary_color }
    : undefined;
  const isActingAs = principal?.acting_as_client_id != null;

  const isDefaultBrand = appName === DEFAULT_APP_NAME && !hideSkyxops;
  const hasLogo = !!branding?.logo_blob_id;
  const left = (
    <div className="flex items-center gap-3">
      {isDefaultBrand ? (
        <img src={SKYXOPS_LOGO} alt="SKYXOPS" className="h-6 w-auto" />
      ) : hasLogo ? (
        <img
          src={`/api/v1/launcher/branding/logo?v=${encodeURIComponent(branding?.logo_blob_id ?? '')}`}
          alt={appName}
          className="h-8 max-h-8 w-auto object-contain"
        />
      ) : (
        <span className="text-xl font-bold tracking-tight">{appName}</span>
      )}
      <span className="hidden border-l border-white/25 pl-3 text-base font-semibold uppercase tracking-wide text-white/90 sm:block">
        <FormattedMessage id="header.launcher_eyebrow" />
      </span>
      {eyebrow && <span className="text-xs italic text-white/60">{eyebrow}</span>}
      {!hideSkyxops && appName !== DEFAULT_APP_NAME && (
        <span className="border-l border-white/25 pl-3 text-[10px] font-medium uppercase tracking-wider text-white/40">
          <FormattedMessage id="header.on_skyxops" />
        </span>
      )}
    </div>
  );

  const right = (
    <>
      <AccountMenu principal={principal} onLogout={onLogout} />
    </>
  );

  const banner = isActingAs ? (
    <div className="bg-amber-500 px-6 py-2 text-sm font-medium text-amber-950">
      {/* AI-LAUNCH-42: the "exit impersonation" link was an <a href="#"> with no
          handler — it silently did nothing, so an operator clicking it believed
          they had exited when they had not. The real flow lives in the msp app
          (POST /api/v1/msp/exit-impersonation), a different origin; linking to it
          needs a configured app URL rather than rewriting the browser hostname,
          which is what AI-LAUNCH-40 introduces. Until then the banner states the
          fact without offering a control that does not work. */}
      <FormattedMessage
        id="header.acting_as_banner"
        values={{ client_id: principal!.acting_as_client_id ?? 0 }}
      />
    </div>
  ) : null;

  return { left, right, banner, headerStyle };
}
